import React, { useCallback, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { t as tI18n } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import ProjectAPI from "../../api/ProjectAPI";
import Input from "../form/Input";
import Select from "../form/Select";
import Modal from "../modal/Modal";

import type { UploadFromUrlData } from "../../api/EncoderAPI.types";

const URL_REGEX =
  /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%@_.~+&:]*)*(\?[;&a-z\d%@_.,~+&:=-]*)?(#[-a-z\d_]*)?$/i;
const S3_REGEX = /^s3:\/\//i;

// Sentinel option letting the user enter unregistered credentials by hand
// instead of picking a saved storage.
const MANUAL_ENTRY = "manual_entry";

// The schema depends on whether saved storages exist: with storages the user
// must pick one (or the manual entry); without them the S3 credentials are
// entered by hand as before.
function buildFormSchema(hasStorages: boolean) {
  return z
    .object({
      url: z
        .string()
        .max(512)
        .refine(
          (val) => URL_REGEX.test(val) || S3_REGEX.test(val),
          tI18n("editor-content:uploadFromURLModal.urlError"),
        ),
      storage_credential: z
        .object({ value: z.string(), label: z.string() })
        .nullable(),
      access_key: z.string(),
      secret_key: z.string(),
      region: z.string(),
      host: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!S3_REGEX.test(data.url)) return;

      const selectedStorage = data.storage_credential?.value;

      if (hasStorages) {
        if (!selectedStorage) {
          ctx.addIssue({
            code: "custom",
            path: ["storage_credential"],
            message: tI18n("editor-errors:requiredField"),
          });
          return;
        }
        if (selectedStorage !== MANUAL_ENTRY) return;
      }

      if (!data.access_key)
        ctx.addIssue({
          code: "custom",
          path: ["access_key"],
          message: tI18n("editor-errors:requiredField"),
        });
      if (!data.secret_key)
        ctx.addIssue({
          code: "custom",
          path: ["secret_key"],
          message: tI18n("editor-errors:requiredField"),
        });
      if (!data.region)
        ctx.addIssue({
          code: "custom",
          path: ["region"],
          message: tI18n("editor-errors:requiredField"),
        });
    });
}

type FormValues = z.infer<ReturnType<typeof buildFormSchema>>;

interface UploadFromUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UploadFromUrlData) => void;
}

export default function UploadFromUrlModal({
  isOpen,
  onClose,
  onSubmit,
}: UploadFromUrlModalProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const storageOptionsQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getStorageAccessOptions,
    params: [],
  });
  const { data: storageOptionsData, isLoading: isStoragesLoading } = useQuery({
    ...storageOptionsQuery,
    enabled: isOpen,
  });

  const storages = useMemo(
    () => storageOptionsData?.data ?? [],
    [storageOptionsData],
  );
  const hasStorages = storages.length > 0;

  const storageSelectOptions = useMemo(
    () => [
      ...storages.map((storage) => ({
        value: storage.uuid,
        label: storage.label,
      })),
      { value: MANUAL_ENTRY, label: t("uploadFromURLModal.manualEntry") },
    ],
    [storages, t],
  );

  const formSchema = useMemo(() => buildFormSchema(hasStorages), [hasStorages]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: "",
      storage_credential: null,
      access_key: "",
      secret_key: "",
      region: "",
      host: "",
    },
  });

  const isS3 = S3_REGEX.test(methods.watch("url"));
  const isManualSelected =
    methods.watch("storage_credential")?.value === MANUAL_ENTRY;

  const showStorageSelect = isS3 && (isStoragesLoading || hasStorages);
  const showManualFields =
    isS3 && !isStoragesLoading && (!hasStorages || isManualSelected);

  const handleClose = useCallback((): void => {
    methods.reset();
    onClose();
  }, [methods, onClose]);

  function handleSubmit(data: FormValues): void {
    const parts = data.url.split("/");
    const mediaName = parts[parts.length - 1] ?? "media";

    if (!isS3) {
      onSubmit({ media_name: mediaName, media_link: data.url });
      handleClose();
      return;
    }

    const useManualCredentials =
      !hasStorages || data.storage_credential?.value === MANUAL_ENTRY;

    const s3 = useManualCredentials
      ? {
          url: data.url,
          host: data.host,
          region: data.region,
          credentials: {
            access_key: data.access_key,
            secret_key: data.secret_key,
          },
        }
      : {
          url: data.url,
          storage_credential_uuid: data.storage_credential?.value,
        };

    onSubmit({ media_name: mediaName, s3 });
    handleClose();
  }

  return (
    <Modal
      isDivided
      withDarkBackground
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      actions={[
        {
          label: t("uploadFromURLModal.cancel"),
          buttonVariant: "secondary",
          onClick: handleClose,
          isCloseAfterAction: false,
        },
        {
          label: t("uploadFromURLModal.confirm"),
          onClick: methods.handleSubmit(handleSubmit),
          isCloseAfterAction: false,
        },
      ]}
      header={
        <span className="text-foreground text-[1.171875rem] font-semibold">
          {t("uploadFromURLModal.header")}
        </span>
      }
    >
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <div className="flex flex-col gap-[0.78125rem]">
          <Input
            isRequired
            label={t("uploadFromURLModal.urlLabel")}
            name="url"
            placeholder="https://… or s3://…"
          />
          {showStorageSelect ? (
            <Select
              isPortal
              isRequired
              isLoading={isStoragesLoading}
              label={t("uploadFromURLModal.storageLabel")}
              name="storage_credential"
              options={storageSelectOptions}
              placeholder={t("uploadFromURLModal.storagePlaceholder")}
            />
          ) : null}
          {showManualFields ? (
            <React.Fragment>
              <Input
                isRequired
                label={t("uploadFromURLModal.accessKeyLabel")}
                name="access_key"
              />
              <Input
                isFullWidth
                isRequired
                label={t("uploadFromURLModal.secretKeyLabel")}
                name="secret_key"
                type="password"
              />
              <Input
                isRequired
                label={t("uploadFromURLModal.regionLabel")}
                name="region"
              />
              <Input label={t("uploadFromURLModal.hostLabel")} name="host" />
            </React.Fragment>
          ) : null}
        </div>
      </FormProvider>
    </Modal>
  );
}
